import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import { CopySuggestion } from '@marketing-milk/interfaces'

type Props = {
  onImportAsCreative: (name: string, copy?: CopySuggestion) => Promise<void>
  onClose: () => void
  initialCopy?: CopySuggestion
}

export function ImportModal({ onImportAsCreative, onClose, initialCopy }: Props) {
  const [name, setName] = useState('')
  const [importing, setImporting] = useState(false)

  const handleImport = async () => {
    if (!name.trim()) return
    setImporting(true)
    try {
      await onImportAsCreative(name.trim(), initialCopy)
      onClose()
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Create Creative</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fa fa-times text-lg" />
          </button>
        </div>

        {initialCopy && (
          <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 flex flex-col gap-1.5">
            <p className="text-xs font-semibold text-purple-700 mb-1">AI Copy Selected</p>
            {initialCopy.headline && (
              <p className="text-xs text-gray-700">
                <span className="font-medium text-gray-500">Headline:</span> {initialCopy.headline}
              </p>
            )}
            {initialCopy.description && (
              <p className="text-xs text-gray-700">
                <span className="font-medium text-gray-500">Description:</span>{' '}
                {initialCopy.description}
              </p>
            )}
            {initialCopy.graphicText && (
              <p className="text-xs text-gray-700">
                <span className="font-medium text-gray-500">Graphic Text:</span>{' '}
                {initialCopy.graphicText}
              </p>
            )}
            {initialCopy.primaryText && (
              <p className="text-xs text-gray-700">
                <span className="font-medium text-gray-500">Primary Text:</span>{' '}
                {initialCopy.primaryText}
              </p>
            )}
            <p className="text-xs text-gray-700">
              <span className="font-medium text-gray-500">CTA:</span> {initialCopy.callToAction}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-gray-700">Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Summer Sale — AI Generated v1"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={importing}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!name.trim() || importing}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {importing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <i className="fa fa-file-import" />
            )}
            Create Creative
          </button>
        </div>
      </div>
    </div>
  )
}
